import { createSlice } from '@reduxjs/toolkit'
import { useSelector } from 'react-redux'
import { rootReducer, type AppDispatch } from '../store'
import { calcApi } from './calc'

const forceRefetch = { forceRefetch: true } as const

export function refetchApplicationCalculationData(
  dispatch: AppDispatch,
  orderId: string | number | null | undefined,
) {
  if (orderId == null || orderId === '') {
    return
  }
  const id = String(orderId)
  dispatch(calcApi.endpoints.orderById.initiate(id, forceRefetch))
  dispatch(calcApi.endpoints.summaryByOrderId.initiate(id, forceRefetch))
  dispatch(calcApi.endpoints.getOrderSummaryById.initiate(id, forceRefetch))
  dispatch(calcApi.endpoints.getOrderSummaryInvoicesById.initiate(id, forceRefetch))
  dispatch(calcApi.endpoints.invoicesByOrderId.initiate(id, forceRefetch))
  dispatch(calcApi.endpoints.getRouteByOrderId.initiate(id, forceRefetch))
  dispatch(calcApi.endpoints.declarationsByOrderId.initiate(id, forceRefetch))
  dispatch(calcApi.endpoints.getBankCommissionsByOrderId.initiate({ orderId: id }, forceRefetch))
  dispatch(calcApi.endpoints.getBrokerDataByCode.initiate(id, forceRefetch))
  dispatch(calcApi.endpoints.getExtraServicesByOrderId.initiate(id, forceRefetch))
}

export const applicationCalculationsSlice = createSlice({
  name: 'applicationCalculations',
  initialState: {
    byOrderId: {} as Record<string, ApplicationCalculationData>,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      calcApi.endpoints.orderById.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          order: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.summaryByOrderId.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          packagingSummary: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getOrderSummaryById.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          summaryInfo: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getOrderSummaryInvoicesById.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          summaryInvoices: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.invoicesByOrderId.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          invoices: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getRouteByOrderId.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          route: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.declarationsByOrderId.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          declarations: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getBankCommissionsByOrderId.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs.orderId)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          bankCommissions: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getBrokerDataByCode.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          certification: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getExtraServicesByOrderId.matchFulfilled,
      (state, { payload, meta }) => {
        const orderId = String(meta.arg.originalArgs)
        state.byOrderId[orderId] = {
          ...state.byOrderId[orderId],
          extraServices: payload,
        }
      }
    )
  },
})

export const useApplicationCalculationData = (orderId: string) => {
  return useSelector(({ applicationCalculations }: ReturnType<typeof rootReducer>) => (
    applicationCalculations.byOrderId[orderId]
  ))
}
